import Button from '@/components/Button';
import Portal from '@/components/Portal';
import Toggle from '@/components/Toggle';
import useModalStack from '@/components/hooks/useModalStack';
import { FiInfo, FiX } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import ScreenBreadcrumbV2 from '@/components/v2/ScreenBreadcrumbV2';
import statusIconMap from '@/schema/statusIconMap';
import { randomId } from '@shared/utils/randomId.js';
import { AnimatePresence, m } from 'framer-motion';
import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react';
import useMeasure from 'react-use-measure';

/**
 * ModalV2 Component
 * @param {Object} props
 * @param {boolean} props.show - Controls modal visibility
 * @param {string|React.RefObject} [props.autoFocusRef] - Selector string or ref to auto-focus element when modal opens
 * @param {string} [props.title] - Modal title text
 * @param {number|string} [props.number] - Number to display next to title
 * @param {string} [props.description] - Description text below title
 * @param {React.ReactNode} [props.children] - Modal body content
 * @param {React.ReactNode} [props.footer] - Custom footer content
 * @param {string} [props.message] - Message text to display above footer
 * @param {string} [props.messageType] - Message type: 'success', 'error', 'warning', 'info'
 * @param {Array<Object>} [props.actions] - Array of action button props (passed to Button component)
 * @param {string} [props.className] - Additional CSS class names
 * @param {boolean} [props.portal=false] - Whether to render in a portal
 * @param {boolean} [props.animateHeight=false] - Enable height animation with framer-motion
 * @param {string|number} [props.height] - Fixed height of modal content
 * @param {string|number} [props.width] - Width of modal content
 * @param {string|number} [props.minHeight] - Minimum height of modal content
 * @param {string|number} [props.maxHeight] - Maximum height of modal content
 * @param {string|number} [props.maxWidth] - Maximum width of modal content
 * @param {boolean} [props.useHeader=false] - Use header styling
 * @param {Function} [props.onHide] - Callback when modal should close
 * @param {boolean} [props.repeatable=false] - Show "Create more" toggle in footer
 * @param {boolean} [props.isRepeatable] - Controlled state for repeatable toggle
 * @param {Function} [props.toggleIsRepeatable] - Callback for repeatable toggle
 * @param {boolean} [props.persistent=false] - Hide close button if true
 * @param {boolean} [props.centered=false] - Center modal vertically
 * @param {boolean} [props.isFullScreen=false] - Make modal full screen
 * @param {boolean} [props.noHeader=false] - Hide header section
 * @param {boolean} [props.noFooter=false] - Hide footer section
 * @param {React.ReactNode} [props.header] - Custom header content
 * @param {Array<Object>} [props.headerActions=[]] - Array of action button props for header
 * @param {string} [props.titleInfo] - Info tooltip text for title
 * @param {Function} [props.onTitleInfoClick] - Callback when title info is clicked
 * @param {string|number} [props.minBodyHeight] - Minimum height for body section
 * @param {string|number} [props.padding] - Padding value for content
 * @param {Array} [props.breadcrumb] - Breadcrumb stack array for ScreenBreadcrumbV2
 */
const ModalV2Component = ({ show, autoFocusRef, title, number, description, children, footer, message, messageType, actions, className, portal = false, animateHeight = false, height, width, minHeight, maxHeight, maxWidth, useHeader = false, onHide = () => {}, repeatable = false, isRepeatable, toggleIsRepeatable, persistent = false, centered = false, isFullScreen = false, noHeader = false, noFooter = false, header, headerActions = [], titleInfo, onTitleInfoClick, minBodyHeight, padding, breadcrumb }) => {
  const id = useRef(undefined);
  const modalRef = useRef(null);
  const transition = { duration: 0.2, type: 'spring', bounce: 0 };
  if (!id.current) {
    id.current = `modal-v2-${randomId()}`;
  }
  const [ref, bounds] = useMeasure();
  const calculatedZIndex = useModalStack(id.current, show);
  const [isContentReady, setIsContentReady] = useState(false);
  const previousBoundsHeight = useRef(0);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        const activeElement = document.activeElement;
        if (activeElement && ['INPUT', 'TEXTAREA'].includes(activeElement.nodeName) && activeElement.value.trim() !== '') {
          activeElement.blur?.();
          return;
        }

        onHide();
      }
    },
    [onHide]
  );

  useEffect(() => {
    if (show) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [show, handleKeyDown]);

  useEffect(() => {
    setTimeout(() => {
      if (show && autoFocusRef) {
        if (typeof autoFocusRef === 'string') {
          document.querySelector(`.comp-modal-v2 ${autoFocusRef}`)?.focus();
        } else {
          autoFocusRef?.current?.focus();
        }
      }
    }, 100);
  }, [show]);

  // Wait for content to be measured before showing animation (only for animateHeight)
  useEffect(() => {
    if (!animateHeight) {
      return;
    }

    if (!show) {
      setIsContentReady(false);
      previousBoundsHeight.current = 0;
      return;
    }

    // For animateHeight, wait for bounds to stabilize
    if (bounds.height > 0) {
      // Check if height has changed (content loaded) or if it's the first measurement
      if (bounds.height !== previousBoundsHeight.current) {
        const newHeight = bounds.height;
        previousBoundsHeight.current = newHeight;
        // Wait for height to stabilize (content fully loaded)
        const timer = setTimeout(() => {
          // Check if height is still the same (content has finished loading)
          if (bounds.height === newHeight) {
            setIsContentReady(true);
          }
        }, 100);
        return () => clearTimeout(timer);
      } else if (previousBoundsHeight.current > 0 && !isContentReady) {
        // Height is stable, content is ready
        setIsContentReady(true);
      }
    }
  }, [show, bounds.height, animateHeight, isContentReady]);

  const PortalComponent = portal ? Portal : Fragment;
  const portalProps = portal ? { id: id.current } : {};
  const footerClass = footer && actions && actions.length > 0 ? 'footer-with-actions' : footer ? 'footer-no-actions' : '';
  const footerIcon = statusIconMap(messageType);

  if (!animateHeight && !show) {
    return null;
  }

  if (animateHeight) {
    // Use bounds.height for animation, start from 0 if not ready
    const targetHeight = bounds.height > 0 ? bounds.height : 0;

    return (
      <AnimatePresence mode={'sync'}>
        <PortalComponent {...portalProps}>
          {show && (
            <m.div className={`comp-modal-v2${className ? ` ${className}` : ''} ${show ? 'show' : ''}`} style={{ zIndex: calculatedZIndex }}>
              <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className={'background'} onClick={onHide} />
              <m.div
                ref={modalRef}
                tabIndex={-1}
                initial={{ opacity: 0, y: 10, height: 0 }}
                animate={isContentReady ? { height: targetHeight, opacity: 1, y: 0 } : { height: targetHeight, opacity: 0, y: 10 }}
                exit={{ opacity: 0, y: 10, height: 0 }}
                transition={transition}
                layout={true}
                className={`content ${useHeader ? 'use-header' : ''} ${centered ? 'centered' : ''} ${isFullScreen ? 'full-screen' : ''}`}
                style={{
                  width: maxWidth || width,
                  maxWidth,
                  minHeight,
                  maxHeight,
                  padding,
                  ...(padding ? { backgroundColor: '#fff' } : {}),
                  ...(maxHeight ? { overflowY: 'auto' } : {})
                }}>
                <div ref={ref}>
                  {!noHeader && (
                    <div className={'header'}>
                      {breadcrumb ? (
                        <ScreenBreadcrumbV2 stack={breadcrumb} hoverEffect={true} />
                      ) : (
                        <div className={'header-content-left'}>
                          <div className={'title'}>
                            {title}
                            {!!number && <span className={'number'}>{number}</span>}
                            {!!titleInfo && (
                              <div className={'title-info'} data-title={titleInfo} onClick={onTitleInfoClick}>
                                <IconV2 icon={FiInfo} size={14} />
                              </div>
                            )}
                          </div>
                          {description && <div className={'description'}>{description}</div>}
                        </div>
                      )}
                      {(header || (headerActions && headerActions.length > 0)) && (
                        <div className={'header-content-right'}>
                          {header}
                          {headerActions?.map((action, index) => (
                            <Button key={index} {...action} />
                          ))}
                        </div>
                      )}
                      {!persistent && (
                        <div role={'button'} className={'close-button'} onClick={onHide} data-title={'Close'}>
                          <IconV2 icon={FiX} />
                        </div>
                      )}
                    </div>
                  )}
                  <div className={'body'} style={{ minHeight: minBodyHeight }}>
                    {children}
                  </div>
                  {message && (
                    <div className={`message ${messageType}`}>
                      <IconV2 icon={footerIcon} />
                      <span>{message}</span>
                    </div>
                  )}
                  {!noFooter && (
                    <div className={`footer ${footerClass}`}>
                      {repeatable && (
                        <div className={'footer-content repeatable'}>
                          <Toggle active={isRepeatable} onClick={toggleIsRepeatable} size={'xs'} />
                          <span>Create more</span>
                        </div>
                      )}
                      {footer && <div className={'footer-content'}>{footer}</div>}
                      {actions && actions.length > 0 && (
                        <div className={'actions'}>
                          {actions.map((action, index) => (
                            <Button key={index} {...action} />
                          ))}
                        </div>
                      )}
                      {!footer && (!actions || actions?.length === 0) && <Button onClick={onHide}>Close</Button>}
                    </div>
                  )}
                </div>
              </m.div>
            </m.div>
          )}
        </PortalComponent>
      </AnimatePresence>
    );
  }

  return (
    <PortalComponent {...portalProps}>
      <div className={`comp-modal-v2 css-animate${className ? ` ${className}` : ''} ${show ? 'visible' : ''}`} style={{ zIndex: calculatedZIndex }}>
        <div className={'background'} onClick={onHide} />
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`content ${useHeader ? 'use-header' : ''} ${centered ? 'centered' : ''} ${isFullScreen ? 'full-screen' : ''}`}
          style={{
            height,
            width: maxWidth || width,
            maxWidth,
            minHeight,
            maxHeight,
            padding,
            ...(padding ? { backgroundColor: '#fff' } : {}),
            ...(maxHeight ? { overflowY: 'auto' } : {})
          }}>
          <div ref={ref}>
            {!noHeader && (
              <div className={'header'}>
                {breadcrumb ? (
                  <ScreenBreadcrumbV2 stack={breadcrumb} hoverEffect={true} />
                ) : (
                  <div className={'header-content-left'}>
                    <div className={'title'}>
                      {title}
                      {!!number && <span className={'number'}>{number}</span>}
                      {!!titleInfo && (
                        <div className={'title-info'} data-title={titleInfo} onClick={onTitleInfoClick}>
                          <IconV2 icon={FiInfo} size={14} />
                        </div>
                      )}
                    </div>
                    {description && <div className={'description'}>{description}</div>}
                  </div>
                )}
                {(header || (headerActions && headerActions.length > 0)) && (
                  <div className={'header-content-right'}>
                    {header}
                    {headerActions?.map((action, index) => (
                      <Button key={index} {...action} />
                    ))}
                  </div>
                )}
                {!persistent && (
                  <div role={'button'} className={'close-button'} onClick={onHide} data-title={'Close'}>
                    <IconV2 icon={FiX} />
                  </div>
                )}
              </div>
            )}
            <div className={'body'} style={{ minHeight: minBodyHeight }}>
              {children}
            </div>
            {message && (
              <div className={`message ${messageType}`}>
                <IconV2 icon={footerIcon} />
                <span>{message}</span>
              </div>
            )}
            {!noFooter && (
              <div className={`footer ${footerClass}`}>
                {repeatable && (
                  <div className={'footer-content repeatable'}>
                    <Toggle active={isRepeatable} onClick={toggleIsRepeatable} size={'xs'} />
                    <span>Create more</span>
                  </div>
                )}
                {footer && <div className={'footer-content'}>{footer}</div>}
                {actions && actions.length > 0 && (
                  <div className={'actions'}>
                    {actions.map((action, index) => (
                      <Button key={index} {...action} />
                    ))}
                  </div>
                )}
                {!footer && (!actions || actions?.length === 0) && <Button onClick={onHide}>Close</Button>}
              </div>
            )}
          </div>
        </div>
      </div>
    </PortalComponent>
  );
};

ModalV2Component.displayName = 'ModalV2';

const ModalV2 = memo(ModalV2Component);

export default ModalV2;
